import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AlertTriangle } from 'lucide-react';

import { $deleteFile } from '../../_state/fileActions.state';
import {
  $selectedFileForAction,
  $showDeleteDialog,
} from '../../_state/fileDialogs.state';
import { $files } from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FileDeleteDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showDeleteDialog);
  const selectedFileId = useAtomValue($selectedFileForAction);
  const files = useAtomValue($files);
  const deleteFile = useSetAtom($deleteFile);

  const selectedFile = files.find((f) => f.id === selectedFileId);

  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async () => {
    if (!selectedFileId) {
      toast.error(t.dialogs.delete.errorNoFile);
      return;
    }

    setIsDeleting(true);

    try {
      await deleteFile({ fileId: selectedFileId });
      toast.success(
        t.dialogs.delete.successDeleted.replace(
          '{name}',
          selectedFile?.name || '',
        ),
      );
      setIsOpen(false);
    } catch (error) {
      toast.error(
        t.dialogs.delete.errorFailed.replace(
          '{error}',
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!selectedFile) {
    return null;
  }

  const isFolder = selectedFile.type === 'Directory';

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileDeleteDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-failure">
            <AlertTriangle className="size-5" />
            {isFolder
              ? t.dialogs.delete.titleFolder
              : t.dialogs.delete.titleFile}
          </DialogTitle>
        </DialogHeader>

        <div className="p-4">
          <p className="text-sm text-neutral/70">
            {t.dialogs.delete.message.replace('{name}', selectedFile.name)}
          </p>
          {isFolder && (
            <p className="mt-2 text-sm text-failure/80">
              {t.dialogs.delete.warningFolder}
            </p>
          )}
          <p className="mt-2 text-sm text-neutral/60">
            {t.dialogs.delete.warningPermanent}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onPress={handleClose}
            isDisabled={isDeleting}
          >
            {t.dialogs.delete.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleDelete}
            isDisabled={isDeleting}
            isLoading={isDeleting}
            className="bg-failure text-white hover:bg-failure/90"
          >
            {t.dialogs.delete.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
